import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, ShieldCheck, KeyRound, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { changeMyPassword, updateMyProfile } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import NotificationPreferencesCard from './NotificationPreferencesCard';

const inputClass =
  'h-10 border-[rgba(6,43,26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212,175,55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';

function errorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}

export default function AccountSettingsCard() {
  const { profile, refreshProfile } = useAuth();
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    const parts = String(profile?.full_name || '').split(' ').filter(Boolean);
    setProfileForm({
      first_name: profile?.first_name || parts[0] || '',
      last_name: profile?.last_name || (parts.length > 1 ? parts.slice(1).join(' ') : ''),
      email: profile?.email || '',
    });
  }, [profile]);

  const updateProfileField = (key) => (event) => {
    setProfileForm((form) => ({ ...form, [key]: event.target.value }));
  };

  const updatePasswordField = (key) => (event) => {
    setPasswordForm((form) => ({ ...form, [key]: event.target.value }));
  };

  const submitProfile = async (event) => {
    event.preventDefault();
    if (!profileForm.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.email.trim())) {
      toast.error('Enter a valid email address');
      return;
    }
    try {
      setSavingProfile(true);
      await updateMyProfile({
        first_name: profileForm.first_name.trim(),
        last_name: profileForm.last_name.trim(),
        email: profileForm.email.trim().toLowerCase(),
      });
      await refreshProfile();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not update profile'));
    } finally {
      setSavingProfile(false);
    }
  };

  const submitPassword = async (event) => {
    event.preventDefault();
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    try {
      setSavingPassword(true);
      await changeMyPassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password updated');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not update password'));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <><Card className="border border-[rgba(6,43,26,0.08)] shadow-sm" data-testid="account-settings-card">
      <CardHeader className="border-b border-[rgba(6,43,26,0.08)]">
        <CardTitle
          className="flex items-center gap-2 text-base font-semibold text-[#062B1A]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          <ShieldCheck className="h-4 w-4 text-[#D4AF37]" />
          Account settings
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6 pt-6 lg:grid-cols-2">
        <form onSubmit={submitProfile} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="profile-first-name" className="text-sm font-medium text-[#062B1A]">
                First name
              </Label>
              <Input
                id="profile-first-name"
                value={profileForm.first_name}
                onChange={updateProfileField('first_name')}
                className={inputClass}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-last-name" className="text-sm font-medium text-[#062B1A]">
                Last name
              </Label>
              <Input
                id="profile-last-name"
                value={profileForm.last_name}
                onChange={updateProfileField('last_name')}
                className={inputClass}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-email" className="text-sm font-medium text-[#062B1A]">
              Email
            </Label>
            <Input
              id="profile-email"
              type="email"
              value={profileForm.email}
              onChange={updateProfileField('email')}
              className={inputClass}
            />
          </div>
          <Button
            type="submit"
            disabled={savingProfile}
            className="h-10 bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            {savingProfile ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save profile
          </Button>
        </form>

        <form onSubmit={submitPassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password" className="text-sm font-medium text-[#062B1A]">
              Current password
            </Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={passwordForm.current_password}
              onChange={updatePasswordField('current_password')}
              className={inputClass}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-sm font-medium text-[#062B1A]">
                New password
              </Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={passwordForm.new_password}
                onChange={updatePasswordField('new_password')}
                className={inputClass}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-sm font-medium text-[#062B1A]">
                Confirm password
              </Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={passwordForm.confirm_password}
                onChange={updatePasswordField('confirm_password')}
                className={inputClass}
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={savingPassword}
            variant="outline"
            className="h-10 border-[rgba(6,43,26,0.16)] text-[#062B1A] hover:bg-[#F7F8FA]"
          >
            {savingPassword ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
            Change password
          </Button>
        </form>
      </CardContent>
    </Card><NotificationPreferencesCard /></>
  );
}
